/** @jsx isolateComponent */

import { css } from '@emotion/core';
import {
  Animation,
  Colors,
  Icon,
  Link,
  Typography,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { UniversalNavLinkType } from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';

export type Props = {
  /**
   * Array of links to render as a submenu.
   */
  links: Array<UniversalNavLinkType>;
  /**
   * The toggle text of the submenu.
   */
  text: string;
};

const Styles = labelStyles('UniversalSidebarSubmenu', {
  summary: [
    css`
      display: block;
      border-radius: 15px;
      color: ${Colors.neutral700};

      &:hover,
      &:focus {
        color: ${Colors.neutral700};
        background-color: ${Colors.cream};
      }
      &:focus {
        outline: solid 2px ${Colors.brand};
      }
      outline: solid 2px transparent;
      transition: ${Animation.transitionValue};
    `,
    css`
      /* Hides default icon on details element */
      &::-webkit-details-marker {
        display: none;
      }
      /* Hide default icon from IE. */
      &::before {
        display: none;
      }
    `,
  ],

  // Wrapper so layout works in Safari.
  summaryInner: css`
    ${Typography.paragraph3Medium};
    line-height: 20px;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
    min-height: 50px;
    padding: 10px 15px;
    white-space: nowrap;
    width: 100%;
    transition: ${Animation.transitionValue};
  `,

  caret: css`
    transition: ${Animation.transitionValue};

    [open] & {
      transform: rotate(-180deg);
    }
  `,

  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
    padding: 10px 0;
  `,

  link: css`
    ${Typography.paragraph3Medium};
    display: block;
    border-radius: 3px;
    color: ${Colors.neutral700};
    line-height: 20px;
    padding: 10px 15px;
    text-decoration: none;
    color: ${Colors.neutral700};
    outline: solid 2px transparent;
    transition: ${Animation.transitionValue};

    &:hover,
    &:focus {
      background-color: ${Colors.cream};
      text-decoration: none;
    }
    &:hover {
      outline: solid 2px transparent;
    }
    &:focus {
      outline: solid 2px ${Colors.brand};
    }
  `,

  linkInner: css`
    transform: translateY(1px);
    padding-left: 16px;
  `,

  linkTitle: css`
    display: block;
    text-decoration: none;
    outline: none;
  `,

  linkDescription: css`
    display: block;
    color: ${Colors.neutral700};
    margin-top: 5px;
    font-size: 12px;
    line-height: 15px;
    text-decoration: none;
    outline: none;
  `,
});

const SubmenuLink = (props: UniversalNavLinkType) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  return (
    <Link
      color={null}
      css={Styles.link}
      href={props.href ? getFullyQualifiedURL(props.href) : undefined}
    >
      <div css={Styles.linkInner}>
        <p css={Styles.linkTitle}>{props.text}</p>
        {props.description && (
          <p css={Styles.linkDescription}>{props.description}</p>
        )}
      </div>
    </Link>
  );
};

/**
 * @private
 * Renders a submenu within the universal sidebar.
 */
const UniversalSidebarSubmenu = (props: Props) => {
  const linkItems = props.links.map((link: UniversalNavLinkType) => (
    <li key={link.text}>
      <SubmenuLink {...link} />
      {link.submenu && (
        <ul>
          {link.submenu.map((nestedLink) => (
            <li key={nestedLink.text} style={{ paddingLeft: '16px' }}>
              <SubmenuLink {...nestedLink} />
            </li>
          ))}
        </ul>
      )}
    </li>
  ));

  return (
    <details>
      <summary css={Styles.summary}>
        <div css={Styles.summaryInner}>
          <span>{props.text}</span>
          <span css={Styles.caret}>
            <Icon isBlock={true} name="arrowSmallDown" size={24} />
          </span>
        </div>
      </summary>
      <ul css={Styles.list}>{linkItems}</ul>
    </details>
  );
};

export default UniversalSidebarSubmenu;
